import { useEffect, useState } from "react";
import { Plug, Send, Mail, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

type ConnStatus = "unknown" | "connected" | "disconnected" | "checking";

export function Integrations() {
  const { user } = useAuth();
  const [slackStatus, setSlackStatus] = useState<ConnStatus>("unknown");
  const [gmailStatus, setGmailStatus] = useState<ConnStatus>("unknown");

  // Slack form
  const [slackChannel, setSlackChannel] = useState("#general");
  const [slackText, setSlackText] = useState("👋 Hello from P2HM Agents v19");
  const [slackBusy, setSlackBusy] = useState(false);

  // Gmail form
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("Mensagem de P2HM Agents");
  const [text, setText] = useState("Hi! Esta é uma mensagem de teste enviada via Gmail integration.");
  const [gmailBusy, setGmailBusy] = useState(false);

  // Probe inicial: tenta uma chamada minimalista para detectar se conectado
  useEffect(() => {
    if (!user) return;
    probe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function probe() {
    setSlackStatus("checking");
    setGmailStatus("checking");
    // Slack: chama com payload inválido só pra ver se a edge bate em "Slack não conectado"
    try {
      const { data, error } = await supabase.functions.invoke("slack-post", {
        body: { channel: "", text: "" },
      });
      const msg = (error as unknown as { context?: { body?: string } })?.context?.body ?? (data as { error?: string })?.error ?? "";
      const txt = typeof msg === "string" ? msg : JSON.stringify(msg);
      setSlackStatus(txt.toLowerCase().includes("não conectado") ? "disconnected" : "connected");
    } catch {
      setSlackStatus("disconnected");
    }
    try {
      const { data, error } = await supabase.functions.invoke("gmail-send", {
        body: { to: "", subject: "", text: "" },
      });
      const msg = (error as unknown as { context?: { body?: string } })?.context?.body ?? (data as { error?: string })?.error ?? "";
      const txt = typeof msg === "string" ? msg : JSON.stringify(msg);
      setGmailStatus(txt.toLowerCase().includes("não conectado") ? "disconnected" : "connected");
    } catch {
      setGmailStatus("disconnected");
    }
  }

  async function sendSlack() {
    if (!slackChannel || !slackText) return toast.error("Canal e mensagem obrigatórios");
    setSlackBusy(true);
    const { data, error } = await supabase.functions.invoke("slack-post", {
      body: { channel: slackChannel, text: slackText },
    });
    setSlackBusy(false);
    if (error || (data as { error?: string })?.error) {
      const msg = (error as Error)?.message ?? (data as { error?: string })?.error ?? "Falha";
      toast.error(msg);
    } else {
      toast.success("Mensagem enviada para o Slack");
      setSlackStatus("connected");
    }
  }

  async function sendGmail() {
    if (!to || !subject || !text) return toast.error("Preencha todos os campos");
    setGmailBusy(true);
    const { data, error } = await supabase.functions.invoke("gmail-send", {
      body: { to, subject, text },
    });
    setGmailBusy(false);
    if (error || (data as any)?.error) {
      const msg = (error as any)?.message ?? (data as any)?.error ?? "Falha";
      toast.error(msg);
    } else {
      toast.success(`Email enviado para ${to}`);
      setGmailStatus("connected");
    }
  }

  return (
    <div className="h-full overflow-auto p-6 space-y-6 max-w-5xl">
      <div>
        <h2 className="text-2xl font-display font-bold flex items-center gap-2">
          <Plug className="w-6 h-6 text-primary" /> Integrations
        </h2>
        <p className="text-sm text-muted-foreground">
          Conectores reais via Lovable Gateway · OAuth gerenciado · usar em workflows e schedules
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* SLACK */}
        <div className="glass-panel p-5 rounded-lg border border-primary/20 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Send className="w-4 h-4 text-primary" /> Slack
            </h3>
            <StatusBadge status={slackStatus} />
          </div>

          {slackStatus === "disconnected" && (
            <div className="text-xs p-3 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200">
              Slack não está conectado neste workspace Lovable. Peça à IA: <span className="font-mono">"conecte o Slack"</span>{" "}
              (ela usará o connector oficial). Após conectar, clique em <strong>Re-checar</strong>.
            </div>
          )}

          <div>
            <Label className="text-xs">Canal</Label>
            <Input value={slackChannel} onChange={(e) => setSlackChannel(e.target.value)} placeholder="#general ou C1234567" />
          </div>
          <div>
            <Label className="text-xs">Mensagem</Label>
            <Textarea value={slackText} onChange={(e) => setSlackText(e.target.value)} rows={3} />
          </div>
          <div className="flex gap-2">
            <Button onClick={sendSlack} disabled={slackBusy} size="sm" className="bg-gradient-neural border-0 text-primary-foreground gap-1.5">
              {slackBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Enviar
            </Button>
            <Button onClick={probe} size="sm" variant="outline">Re-checar</Button>
          </div>
        </div>

        {/* GMAIL */}
        <div className="glass-panel p-5 rounded-lg border border-primary/20 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" /> Gmail
            </h3>
            <StatusBadge status={gmailStatus} />
          </div>

          {gmailStatus === "disconnected" && (
            <div className="text-xs p-3 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200">
              Gmail não conectado. Peça à IA: <span className="font-mono">"conecte o Gmail"</span>. Envia a partir da SUA caixa de entrada (do builder), não do usuário final.
            </div>
          )}

          <div>
            <Label className="text-xs">Para</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="alguem@exemplo.com" />
          </div>
          <div>
            <Label className="text-xs">Assunto</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Corpo</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} />
          </div>
          <div className="flex gap-2">
            <Button onClick={sendGmail} disabled={gmailBusy} size="sm" className="bg-gradient-neural border-0 text-primary-foreground gap-1.5">
              {gmailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Enviar
            </Button>
            <Button onClick={probe} size="sm" variant="outline">Re-checar</Button>
          </div>
        </div>
      </div>

      <div className="glass-panel p-4 rounded-lg border border-border text-xs text-muted-foreground space-y-3">
        <div>
          <div className="font-mono uppercase text-[10px] tracking-wider mb-2 text-foreground">Como funciona</div>
          Connectors são gerenciados pelo workspace Lovable. Quando conectados, ficam disponíveis como variáveis nas Edge Functions
          (<span className="font-mono">SLACK_API_KEY</span>, <span className="font-mono">GOOGLE_MAIL_API_KEY</span>) com refresh OAuth automático.
          Para conectar, peça à IA: <span className="font-mono">"conecte o Slack"</span> ou <span className="font-mono">"conecte o Gmail"</span>.
        </div>
        <div className="pt-3 border-t border-border">
          <div className="font-mono uppercase text-[10px] tracking-wider mb-2 text-foreground">Bootstrap pg_cron (one-shot)</div>
          <p className="mb-2">Habilita execução automática dos schedules em background. Clique uma vez por projeto.</p>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              const { data, error } = await supabase.functions.invoke("bootstrap-cron", { body: {} });
              if (error || (data as any)?.error) {
                toast.error((error as any)?.message ?? (data as any)?.error ?? "Falha");
              } else {
                toast.success("pg_cron pronto · schedules disparam automaticamente");
              }
            }}
          >
            Habilitar pg_cron automático
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: ConnStatus }) {
  if (status === "connected") {
    return (
      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-success/10 text-success border border-success/30 flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" /> conectado
      </span>
    );
  }
  if (status === "disconnected") {
    return (
      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" /> não conectado
      </span>
    );
  }
  if (status === "checking") {
    return (
      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/30 flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" /> checando
      </span>
    );
  }
  return <span className="text-[10px] font-mono text-muted-foreground">—</span>;
}
